import http from '../../services/httpClient';

/**
 * Every call the Sales Name Check page makes, normalised to
 *   { ok: true, ...body }  |  { ok: false, status, code, error, ...body }
 *
 * settle() is the notInMenu/notInMenuApi.js pattern, copied rather than
 * imported: httpClient throws for 4xx/5xx (error.status, error.data) but
 * RETURNS a 403 as { success: false }, so success is checked explicitly or a
 * denied write would read as done. Unlike the Not-in-Menu failure(), the whole
 * refusal body is kept, because this page needs NEEDS_COLUMNS' sample,
 * DATE_OVERLAP's existing upload, DUPLICATE_FILE's date, ROW_CONFLICT's
 * current row and UNDECIDED's count. Successful writes already fire the
 * app-wide data-changed signal inside httpClient.
 */
const failure = (status, data, fallback) => {
  const body = data && typeof data === 'object' ? data : {};
  return {
    ...body,
    ok: false,
    status,
    code: body.code || 'REQUEST_FAILED',
    error: body.error || fallback,
  };
};

const settle = async (call, fallback) => {
  try {
    const res = await call();
    if (!res || res.success !== true) return failure(403, res, fallback);
    return { ok: true, ...res };
  } catch (err) {
    const body = err && err.data && typeof err.data === 'object' ? err.data : {};
    return failure(err?.status || 0, { ...body, error: body.error || null }, fallback);
  }
};

const BASE = '/sales-name-check';
const enc = encodeURIComponent;

export const getStatus = () =>
  settle(() => http.get(`${BASE}/status`), 'Could not load Sales Name Check.');

export const skipCheck = () =>
  settle(() => http.post(`${BASE}/skip`, {}), 'Could not skip this step.');

/** Re-posted with the owner's answers when the server asks a question (spec §6). */
export const uploadMonthFile = ({ file, columnMap = null, month = null, overlap = null }) => {
  const form = new FormData();
  form.append('file', file);
  if (columnMap) form.append('columnMap', JSON.stringify(columnMap));
  if (month) form.append('month', month);
  if (overlap) form.append('overlap', overlap);
  return settle(() => http.postFormData(`${BASE}/upload`, form), 'Could not read this file.');
};

export const removeUpload = (uploadId) =>
  settle(() => http.delete(`${BASE}/uploads/${enc(uploadId)}`), 'Could not remove this file.');

export const getWorklist = () =>
  settle(() => http.get(`${BASE}/worklist`), 'Could not load the names.');

// '' fallback: the row says "Not saved" itself and appends the server's reason when there is one.
export const saveName = (nameId, body) =>
  settle(() => http.put(`${BASE}/names/${enc(nameId)}`, body), '');

export const saveNamesBulk = (items) =>
  settle(() => http.post(`${BASE}/names/bulk`, { items }), '');

export const addCheck = (nameId) =>
  settle(() => http.get(`${BASE}/names/${enc(nameId)}/add-check`), 'Could not check for similar dishes.');

export const applyCheck = ({ mode, expectedCheckVersion }) =>
  settle(() => http.post(`${BASE}/apply`, { mode, expectedCheckVersion }), 'Apply failed. Nothing was saved.');

export const resetName = (nameId) =>
  settle(() => http.post(`${BASE}/names/${enc(nameId)}/reset`, {}), 'Could not undo this name.');

export const setAside = () =>
  settle(() => http.post(`${BASE}/set-aside`, {}), 'Could not set the check aside.');

export const reopenCheck = () =>
  settle(() => http.post(`${BASE}/reopen`, {}), 'Could not start a new check.');

export const getAddedDishes = () =>
  settle(() => http.get(`${BASE}/added-dishes`), 'Could not load the added dishes.');

/** Same menu read as the Not-in-Menu worklist; MenuItemPicker drops inactive and _bqt dishes itself. */
export const getMenuItems = () =>
  settle(() => http.get('/menu/items?language=en&includeInactive=true'), 'Could not load the menu.');
